import type { LineCitation } from '../../services/ai/citations/citationTypes'

export type LineCitationSurface = 'desktop' | 'mobile'

export interface OpenLineCitationOptions {
  surface: LineCitationSurface
  /**
   * Optional dispatchers wired at the app root. Left as callbacks so the
   * routing helper stays free of context/hook imports (it can be called
   * from anywhere, including outside React).
   */
  onFocusTimelineNode?: (nodeId: string, board?: string) => void
  onOpenUpload?: (uploadId: string) => void
  onScrollToChatTurn?: (turnId: string) => void
  onOpenMeeting?: (meetingId: string) => void
  onOpenReport?: (reportId: string) => void
  onOpenGeneric?: (citation: LineCitation) => void
}

/**
 * Route a citation click to the correct destination for the current surface.
 * Returns `true` when the click was handled, `false` when nothing was
 * dispatched (used by the chip to grey out permanently-unroutable cites).
 */
export const openLineCitationTarget = (
  citation: LineCitation,
  options: OpenLineCitationOptions,
): boolean => {
  const ref = citation.targetRef
  switch (citation.sourceKind) {
    case 'timeline':
      if (ref?.id && options.onFocusTimelineNode) {
        options.onFocusTimelineNode(ref.id, ref.board)
        return true
      }
      break
    case 'checklist':
      if (ref?.id && options.onFocusTimelineNode) {
        options.onFocusTimelineNode(ref.parentId ?? ref.id, ref.board)
        return true
      }
      break
    case 'upload':
      if (ref?.id && options.onOpenUpload) {
        options.onOpenUpload(ref.id)
        return true
      }
      break
    case 'meeting':
      if (ref?.id && options.onOpenMeeting) {
        options.onOpenMeeting(ref.id)
        return true
      }
      break
    case 'report':
      if (ref?.id && options.onOpenReport) {
        options.onOpenReport(ref.id)
        return true
      }
      break
    case 'user_message': {
      const turnId = citation.recordId.startsWith('user_turn:') ? citation.recordId.slice('user_turn:'.length) : null
      if (turnId && options.onScrollToChatTurn) {
        options.onScrollToChatTurn(turnId)
        return true
      }
      break
    }
    default:
      break
  }
  if (options.onOpenGeneric) {
    options.onOpenGeneric(citation)
    return true
  }
  return false
}
